import { Tabs, TabsList, TabsTrigger } from "@devhop/ui";
import { addDays, format, getDay } from "date-fns";
import { PlusIcon } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import type {
  BookingSlot,
  PublicRoom,
  TimetableItem,
} from "../../domain/contracts/public-booking";
import {
  addMinutesToTime,
  formatTimeDisplay,
  GRID_ROW_MINUTES,
  getEventBlockAccentClass,
  getEventCellClass,
  getEventSlotSpan,
  getTimeSlotGroups,
  slotIsEventStart,
  TIME_START_HOUR,
  timeToMinutes,
  toYMD,
} from "./timetable-utils";

const ROW_HEIGHT_PX = 25;
const WEEKDAY_SHORT = ["ອາ", "ຈ", "ອ", "ພ", "ພຫ", "ສຸ", "ສ"];

type PublicTimetableWeekViewProps = {
  weekStart: Date;
  items: TimetableItem[];
  rooms: PublicRoom[];
  onSlotSelect: (slot: BookingSlot) => void;
  onEventClick?: (item: TimetableItem) => void;
  conflictIds?: Set<string>;
  showCurrentTime?: boolean;
};

function getTypeLabel(type: TimetableItem["type"]): string {
  switch (type) {
    case "class":
      return "ຄລາດສ໌";
    case "private":
      return "ວິຊາສ່ວນຕົວ";
    case "room_booking":
      return "ຈອງຫ້ອງ";
    default:
      return "";
  }
}

/** Event is ongoing (current time inside start–end) */
function isEventLive(
  item: TimetableItem,
  dateStr: string,
  currentMinutes: number,
): boolean {
  if (item.date !== dateStr) return false;
  const start = timeToMinutes(item.startTime);
  const end = timeToMinutes(item.endTime);
  return currentMinutes >= start && currentMinutes < end;
}

export function PublicTimetableWeekView({
  weekStart,
  items,
  rooms,
  onSlotSelect,
  onEventClick,
  conflictIds,
  showCurrentTime = true,
}: PublicTimetableWeekViewProps) {
  const [selectedRoomId, setSelectedRoomId] = useState<string>("");

  useEffect(() => {
    const first = rooms[0];
    if (first) {
      const currentExists =
        selectedRoomId && rooms.some((r) => r.id === selectedRoomId);
      if (!currentExists) setSelectedRoomId(first.id);
    }
  }, [rooms, selectedRoomId]);

  const timeSlotGroups = useMemo(() => getTimeSlotGroups(), []);
  const weekDates = useMemo(() => {
    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      dates.push(addDays(weekStart, i));
    }
    return dates;
  }, [weekStart]);

  const [now, setNow] = useState(new Date());
  const todayYMD = format(now, "yyyy-MM-dd");

  useEffect(() => {
    if (!showCurrentTime) return;
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, [showCurrentTime]);

  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const currentSeconds = now.getSeconds();
  const dayStartMinutes = TIME_START_HOUR * 60;
  const dayEndMinutes = 21 * 60;
  const showCurrentTimeLine =
    showCurrentTime &&
    currentMinutes >= dayStartMinutes &&
    currentMinutes < dayEndMinutes;

  // Calculate top position for floating current time line
  // Add ~40px offset for thead height
  const THEAD_HEIGHT = 40;
  const timeLineTop =
    showCurrentTimeLine && currentMinutes >= dayStartMinutes
      ? THEAD_HEIGHT +
        ((currentMinutes - dayStartMinutes) / GRID_ROW_MINUTES) *
          ROW_HEIGHT_PX +
        (currentSeconds / 60 / GRID_ROW_MINUTES) * ROW_HEIGHT_PX
      : null;

  const effectiveRoomId = selectedRoomId || rooms[0]?.id || "";
  const filteredItems = useMemo(() => {
    if (!effectiveRoomId) return [];
    return items.filter((i) => i.roomId === effectiveRoomId);
  }, [items, effectiveRoomId]);

  const filteredItemsByDate = useMemo(() => {
    const map = new Map<string, TimetableItem[]>();
    for (const item of filteredItems) {
      const list = map.get(item.date) ?? [];
      list.push(item);
      map.set(item.date, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.startTime.localeCompare(b.startTime));
    }
    return map;
  }, [filteredItems]);

  const singleRoom = rooms.find((r) => r.id === effectiveRoomId);

  return (
    <div className="flex flex-col overflow-auto">
      <Tabs value={effectiveRoomId} onValueChange={(v) => setSelectedRoomId(v)}>
        <TabsList className="flex h-auto w-full flex-wrap gap-1 rounded-none bg-muted/50 p-1">
          {rooms.map((room) => (
            <TabsTrigger
              key={room.id}
              value={room.id}
              className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              {room.name}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="relative overflow-auto">
        {showCurrentTimeLine && timeLineTop !== null && (
          <div
            className="pointer-events-none absolute right-0 left-0 z-10 flex items-center gap-2 px-2"
            style={{ top: `${timeLineTop}px` }}
          >
            <span className="w-14 shrink-0 text-left font-medium text-primary text-xs">
              {format(now, "HH:mm")}
            </span>
            <div className="size-2 shrink-0 rounded-full bg-primary" />
            <div className="h-0.5 flex-1 bg-primary" />
          </div>
        )}
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="w-16 border-border border-r border-b bg-muted/50 p-1.5 text-left font-medium text-muted-foreground">
                ເວລາ
              </th>
              {weekDates.map((d) => {
                const dateStr = toYMD(d);
                const isToday = dateStr === todayYMD;
                return (
                  <th
                    key={dateStr}
                    className={`min-w-[100px] border-border border-r border-b bg-muted/50 p-1.5 text-center font-medium last:border-r-0 ${isToday ? "text-primary" : ""}`}
                  >
                    <span className="text-muted-foreground text-xs">
                      {WEEKDAY_SHORT[getDay(d)]}
                    </span>
                    <br />
                    <span
                      className={isToday ? "font-semibold text-primary" : ""}
                    >
                      {d.getDate()}
                    </span>
                    {isToday && (
                      <span className="mt-0.5 block size-1 rounded-full bg-primary" />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="relative">
            {singleRoom ? (
              timeSlotGroups.map(([slot1, slot2]) => {
                const slot1End = addMinutesToTime(slot1, GRID_ROW_MINUTES);
                const slot2End = addMinutesToTime(slot2, GRID_ROW_MINUTES);

                const renderDayCells = (slotLabel: string, slotEnd: string) =>
                  weekDates.map((d) => {
                    const dateStr = toYMD(d);
                    const dayItems = filteredItemsByDate.get(dateStr) ?? [];
                    const slotStartMin = timeToMinutes(slotLabel);
                    const slotEndMin = timeToMinutes(slotEnd);
                    const eventStartingHere = dayItems.find(
                      (item) =>
                        item.roomId === singleRoom.id &&
                        slotIsEventStart(item, slotLabel, slotEnd),
                    );
                    const eventCoveringHere = dayItems.find(
                      (item) =>
                        item.roomId === singleRoom.id &&
                        timeToMinutes(item.startTime) < slotEndMin &&
                        timeToMinutes(item.endTime) > slotStartMin &&
                        !slotIsEventStart(item, slotLabel, slotEnd),
                    );

                    if (eventCoveringHere) return null;

                    if (eventStartingHere) {
                      const span = getEventSlotSpan(eventStartingHere);
                      const live = isEventLive(
                        eventStartingHere,
                        dateStr,
                        currentMinutes,
                      );
                      return (
                        <td
                          key={dateStr}
                          rowSpan={span}
                          className={`border-border border-r border-b p-0 align-top last:border-r-0 ${getEventCellClass(eventStartingHere, conflictIds)} ${onEventClick ? "cursor-pointer hover:opacity-90" : ""}`}
                          onClick={() => onEventClick?.(eventStartingHere)}
                          onKeyDown={(e) => {
                            if (
                              onEventClick &&
                              (e.key === "Enter" || e.key === " ")
                            ) {
                              e.preventDefault();
                              onEventClick(eventStartingHere);
                            }
                          }}
                          role={onEventClick ? "button" : undefined}
                          tabIndex={onEventClick ? 0 : undefined}
                        >
                          <div
                            className={`flex h-full flex-col justify-between bg-background py-1.5 pr-2 pl-2 ${getEventBlockAccentClass(eventStartingHere.type, conflictIds, eventStartingHere)}`}
                            style={{
                              minHeight: ROW_HEIGHT_PX * span,
                            }}
                          >
                            <div className="flex items-center justify-between gap-1">
                              <span className="text-muted-foreground text-xs">
                                {getTypeLabel(eventStartingHere.type)}
                              </span>
                              {live && (
                                <span className="rounded bg-primary px-1.5 py-0.5 text-primary-foreground text-xs">
                                  LIVE
                                </span>
                              )}
                            </div>
                            <div className="line-clamp-2 font-medium leading-tight">
                              {eventStartingHere.title}
                            </div>
                            <div className="whitespace-nowrap text-muted-foreground text-xs">
                              {formatTimeDisplay(eventStartingHere.startTime)}
                              {" – "}
                              {formatTimeDisplay(eventStartingHere.endTime)}
                            </div>
                          </div>
                        </td>
                      );
                    }

                    const startTimeStr =
                      slotLabel.length === 5 ? `${slotLabel}:00` : slotLabel;
                    const endTimeStr =
                      slotEnd.length === 5 ? `${slotEnd}:00` : slotEnd;
                    return (
                      <td
                        key={dateStr}
                        className="border-border border-r border-b align-top last:border-r-0"
                      >
                        <button
                          type="button"
                          className="flex h-full min-h-[24px] w-full flex-col items-center justify-center gap-0.5 bg-muted/20 text-muted-foreground transition-colors hover:border-primary/50 hover:bg-muted/40 hover:text-foreground"
                          onClick={() =>
                            onSlotSelect({
                              roomId: singleRoom.id,
                              roomName: singleRoom.name,
                              date: dateStr,
                              startTime: startTimeStr,
                              endTime: endTimeStr,
                            })
                          }
                        >
                          <PlusIcon className="size-3" />
                          {/* <span className="text-[10px]">ຈອງ ({slotLabel})</span> */}
                        </button>
                      </td>
                    );
                  });

                return (
                  <Fragment key={slot1}>
                    <tr
                      className="border-border border-b"
                      style={{ height: ROW_HEIGHT_PX }}
                    >
                      <td
                        rowSpan={2}
                        className="w-16 border-border border-r border-b p-1 align-top text-muted-foreground text-xs"
                      >
                        {slot1}
                      </td>
                      {renderDayCells(slot1, slot1End)}
                    </tr>
                    <tr
                      className="border-border border-b"
                      style={{ height: ROW_HEIGHT_PX }}
                    >
                      {renderDayCells(slot2, slot2End)}
                    </tr>
                  </Fragment>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={1 + weekDates.length}
                  className="border-border border-b p-4 text-center text-muted-foreground text-sm"
                >
                  ບໍ່ມີຫ້ອງ
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
